import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileCheck2, Pencil, Play, Plus, Timer as TimerIcon, Trash2 } from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { Input } from '@/components/input';
import { Menu } from '@/components/menu';
import { Pager } from '@/components/pager';
import { Textarea } from '@/components/textarea';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { useFormError } from '@/lib/form-error';
import {
  useCreateTimeEntry,
  useDeleteTimeEntry,
  useDraftInvoiceFromTime,
  useStartTimer,
  useTimeEntries,
  useUnbilledSummary,
  useUpdateTimeEntry,
} from '@/lib/time-entries';
import { dollars, formatDate as fmtDate, formatDuration, formatTime as fmtTime } from '@/lib/format';
import type { TimeEntryData } from '@/types/api';

function dateInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ClientTime({ clientId }: { clientId: number }) {
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const navigate = useNavigate();

  const { data: paginated, isLoading } = useTimeEntries({ client_id: clientId, page });
  const { data: unbilled } = useUnbilledSummary();
  const start = useStartTimer();
  const draft = useDraftInvoiceFromTime();
  const toast = useToast();

  const entries = paginated?.data ?? [];
  const meta = paginated?.meta;
  const editing = editingId != null ? entries.find((e) => e.id === editingId) ?? null : null;

  const clientRow = useMemo(
    () => (unbilled ?? []).find((r) => r.client_id === clientId) ?? null,
    [unbilled, clientId],
  );

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <div className="grid grid-cols-3 divide-x divide-[var(--color-border)] text-center">
          <div className="px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-[var(--color-foreground-subtle)]">Unbilled time</div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums">
              {formatDuration(clientRow?.total_seconds ?? 0)}
            </div>
          </div>
          <div className="px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-[var(--color-foreground-subtle)]">Unbilled $</div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums">
              {dollars(clientRow?.total_cents ?? 0)}
            </div>
          </div>
          <div className="px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-[var(--color-foreground-subtle)]">Entries shown</div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums">
              {meta?.total ?? entries.length}
            </div>
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        <Button
          variant="secondary"
          disabled={draft.isPending || !clientRow || clientRow.total_seconds === 0}
          onClick={async () => {
            try {
              await draft.mutateAsync({ client_id: clientId });
              toast.success('Draft invoice created');
              navigate(`/clients/${clientId}?tab=approvables`);
            } catch (err) {
              const message =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                'Could not draft invoice.';
              toast.error('Could not draft invoice', message);
            }
          }}
        >
          <FileCheck2 className="size-4" /> Bill this time
        </Button>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" /> Add entry
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={TimerIcon}
          title="No time entries for this client"
          description="Start the timer from the top bar, or add one manually."
        />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]/50 text-left text-[var(--color-foreground-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Date</th>
                <th className="px-4 py-2 font-medium">Description</th>
                <th className="px-4 py-2 font-medium text-right">Duration</th>
                <th className="px-4 py-2 font-medium text-right">Amount</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <Row
                  key={entry.id}
                  entry={entry}
                  onEdit={() => setEditingId(entry.id)}
                  onResume={() =>
                    start.mutate({
                      client_id: entry.client_id,
                      project_id: entry.project_id ?? undefined,
                      card_id: entry.card_id ?? undefined,
                      description: entry.description ?? undefined,
                      billable: entry.billable,
                    })
                  }
                />
              ))}
            </tbody>
          </table>
          {meta ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </Card>
      )}

      <CreateDialog open={createOpen} clientId={clientId} onClose={() => setCreateOpen(false)} />
      <EditDialog entry={editing} onClose={() => setEditingId(null)} />
    </div>
  );
}

function Row({
  entry,
  onEdit,
  onResume,
}: {
  entry: TimeEntryData;
  onEdit: () => void;
  onResume: () => void;
}) {
  const del = useDeleteTimeEntry();
  const confirm = useConfirm();
  const toast = useToast();
  return (
    <tr className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-surface-muted)]/50">
      <td className="whitespace-nowrap px-4 py-2.5">
        <div className="font-medium">{fmtDate(entry.started_at)}</div>
        <div className="text-xs text-[var(--color-foreground-subtle)]">
          {fmtTime(entry.started_at)}
          {entry.ended_at ? ` – ${fmtTime(entry.ended_at)}` : ' – running'}
        </div>
      </td>
      <td className="max-w-[320px] px-4 py-2.5">
        {entry.description ? (
          <span className="line-clamp-2 text-[var(--color-foreground-muted)]">{entry.description}</span>
        ) : (
          <span className="text-xs text-[var(--color-foreground-subtle)]">—</span>
        )}
        {entry.project_id ? (
          <div className="mt-1 text-xs">
            <Link to={`/projects/${entry.project_id}`} className="text-[var(--color-foreground-subtle)] hover:underline">
              {entry.project_name ?? `Project #${entry.project_id}`}
            </Link>
          </div>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-right font-medium tabular-nums">
        {formatDuration(entry.duration_seconds)}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-[var(--color-foreground-muted)]">
        {dollars(entry.amount_cents)}
      </td>
      <td className="px-4 py-2.5">
        {entry.is_running ? (
          <Badge tone="success">Running</Badge>
        ) : entry.billed ? (
          <Badge tone="info">Billed</Badge>
        ) : entry.billable ? (
          <Badge tone="warning">Unbilled</Badge>
        ) : (
          <Badge tone="neutral">Non-billable</Badge>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-right">
        <Menu
          items={[
            { key: 'edit', label: 'Edit', icon: Pencil, onSelect: onEdit, disabled: entry.is_running },
            { key: 'resume', label: 'Start a new timer like this', icon: Play, onSelect: onResume },
            {
              key: 'delete',
              label: 'Delete',
              icon: Trash2,
              danger: true,
              disabled: entry.billed,
              onSelect: async () => {
                const ok = await confirm({
                  title: 'Delete this time entry?',
                  body: `${formatDuration(entry.duration_seconds)} on ${fmtDate(entry.started_at)}.`,
                  confirmLabel: 'Delete entry',
                  tone: 'danger',
                });
                if (ok) {
                  await del.mutateAsync(entry.id);
                  toast.success('Time entry deleted');
                }
              },
            },
          ]}
        />
      </td>
    </tr>
  );
}

function CreateDialog({
  open,
  clientId,
  onClose,
}: {
  open: boolean;
  clientId: number;
  onClose: () => void;
}) {
  const create = useCreateTimeEntry();
  const { error, wrap } = useFormError();
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ok = await wrap(() =>
      create.mutateAsync({
        client_id: clientId,
        started_at: new Date(startedAt).toISOString(),
        ended_at: new Date(endedAt).toISOString(),
        description: description || undefined,
        billable,
      }),
    );
    if (ok) {
      setStartedAt('');
      setEndedAt('');
      setDescription('');
      setBillable(true);
      onClose();
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Add time entry">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Started at</span>
            <Input type="datetime-local" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} required />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Ended at</span>
            <Input type="datetime-local" value={endedAt} onChange={(e) => setEndedAt(e.target.value)} required />
          </label>
        </div>
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-[var(--color-foreground-muted)]">Description</span>
          <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} className="size-4" />
          <span>Billable</span>
        </label>
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Save entry'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function EditDialog({
  entry,
  onClose,
}: {
  entry: TimeEntryData | null;
  onClose: () => void;
}) {
  const update = useUpdateTimeEntry();
  const { error, wrap } = useFormError();
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);
  const [seeded, setSeeded] = useState<number | null>(null);

  if (entry && seeded !== entry.id) {
    setStartedAt(dateInputValue(entry.started_at));
    setEndedAt(entry.ended_at ? dateInputValue(entry.ended_at) : '');
    setDescription(entry.description ?? '');
    setBillable(entry.billable);
    setSeeded(entry.id);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!entry) return;
    const ok = await wrap(() =>
      update.mutateAsync({
        id: entry.id,
        patch: {
          started_at: new Date(startedAt).toISOString(),
          ended_at: endedAt ? new Date(endedAt).toISOString() : null,
          description: description || null,
          billable,
        },
      }),
    );
    if (ok) {
      setSeeded(null);
      onClose();
    }
  }

  return (
    <Dialog
      open={entry != null}
      onClose={() => {
        setSeeded(null);
        onClose();
      }}
      title="Edit time entry"
    >
      {entry ? (
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground-muted)]">Started at</span>
              <Input type="datetime-local" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} required />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-[var(--color-foreground-muted)]">Ended at</span>
              <Input type="datetime-local" value={endedAt} onChange={(e) => setEndedAt(e.target.value)} required />
            </label>
          </div>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Description</span>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} className="size-4" />
            <span>Billable</span>
          </label>
          {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      ) : null}
    </Dialog>
  );
}
